import assetUtils from 'app-lib/assetUtils'
import numberUtils, { autoFormat } from 'app-lib/numberUtils'
import { chainConfigs } from 'bzx-common/src/config/appConfig'
import { Migration } from 'bzx-common/src/stores/MigrationStore'
import { observer } from 'mobx-react'
import React from 'react'
import ButtonChainSwitch from 'shared-components/ButtonChainSwitch'
import { Button, Icon, Input } from 'ui-framework'
import MigrateTokensVM from './MigrateTokensVM'

export function MigrateTokens({ vm, migration }: { vm: MigrateTokensVM; migration: Migration }) {
  const fromInfo = assetUtils.getCommon(migration.details.fromAsset)
  const toInfo = assetUtils.getCommon(migration.details.toAsset)
  const connectedChainId = vm.rootStore.web3Connection.chainId
  const { chainsAvailable } = migration.details
  const toIcon = toInfo.spriteLogo

  if (!connectedChainId) {
    return null
  }

  return (
    <div className="panel--simple md-padded-2 pos-rel">
      <h3 className="title-section flex-row-center panel__header">
        <span>
          Migrate {fromInfo.displayName} to {toInfo.displayName}{' '}
        </span>
        <div className="flex-row-end flex-row-center">
          {chainsAvailable.map((chainId) => (
            <ButtonChainSwitch
              key={chainId}
              onlyIcon
              chainId={chainId}
              className="margin-left-1ch"
            />
          ))}
        </div>
      </h3>

      <div className="margin-bottom-2">
        <div className="margin-bottom-05 flex-row-center">
          <label className="txt-subtitle">{fromInfo.displayName} amount</label>
          <div
            className="txt-subtitle txt-default flex-row-end"
            title={migration.walletBalanceFrom.toFixed()}>
            <Button variant="link" onClick={migration.setMaxAmount}>
              {numberUtils.abbreviate(migration.walletBalanceFrom)}
            </Button>{' '}
            <Icon name="wallet-balance" color="var(--color-txt-muted)" size={20} top={-2} />
          </div>
        </div>

        <div className="input-with-sticked-btn">
          <Input
            onChange={migration.set}
            onChangeEmit="name-value"
            name="amountToConvert"
            value={migration.amountToConvert}
          />
          <Button
            variant="blue"
            onClick={migration.setMaxAmount}
            isLoading={migration.balancesPending}
            disabled={!migration.canConvert}>
            Max
          </Button>
        </div>
      </div>

      <div className="txt-center">
        <p>
          You will receive{' '}
          <b className="txt-positive" title={migration.conversionAmount.toFixed()}>
            {autoFormat(migration.conversionAmount)}
          </b>{' '}
          {toInfo.displayName} <Icon name={toIcon} className="convert__icon" size={24} />
        </p>
        {migration.details.chains[connectedChainId] ? (
          <>
            {migration.needsApproval ? (
              <Button
                variant="green"
                className="btn--responsive"
                isLoading={migration.approvalPending}
                disabled={!migration.enabled}
                onClick={migration.setSpendingApproval}>
                {migration.enabled ? 'Approve Spending' : <i>Soon™</i>}
              </Button>
            ) : (
              <Button
                variant="green"
                className="btn--responsive"
                isLoading={migration.conversionPending}
                disabled={!migration.canConvert}
                value={migration}
                onClick={vm.convert}
                onClickEmit="value">
                {migration.enabled ? 'Migrate' : <i>Soon™</i>}
              </Button>
            )}
            {migration.liquidityLoaded &&
              migration.details.chains[connectedChainId]?.limitedLiquidity && (
                <div className="margin-top-3 panel--alternative padded-1">
                  <b
                    className={
                      migration.liquidityAvailable.gte(migration.amountToConvert)
                        ? 'txt-positive'
                        : 'txt-negative'
                    }>
                    {autoFormat(migration.liquidityAvailable)}
                  </b>{' '}
                  {toInfo.displayName} available for conversion
                </div>
              )}
          </>
        ) : (
          <div className="flex-center">
            <ButtonChainSwitch chainId={chainsAvailable[0]}>
              Switch to {chainConfigs[chainsAvailable[0]].chainDetails.shortName}
            </ButtonChainSwitch>
          </div>
        )}
      </div>
    </div>
  )
}

export default observer(MigrateTokens)
